import { useFormContext } from "react-hook-form";
import { useCallback, useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import { FileUploadFieldProps } from "./FileUpload.types";
import { FormControl, FormDescription, FormField, FormItem, FormMessage } from "@shadcn/components/ui/form";
import { Label } from "@shadcn/components/ui/label";
import { Button } from "@shadcn/components/ui/button";
import { Progress } from "@shadcn/components/ui/progress";
import { useUserJourneyContextOptional } from "@/context/UserJourneyContext";
import { useFormFileUploadEnquiryIdOptional } from "@forms/form-render/contexts/FormFileUploadEnquiryContext";
import { fileUploadApi, type FileUploadInfo } from "./api";
import { getValidationConfig } from "@forms/form-render/helpers/getValidationConfig";
import { getFieldHelperText } from "@forms/form-render/helpers/getFieldHelperText";
import { Upload, X, File } from "lucide-react";
import { cn } from "@shadcn/lib/utils";

interface UploadedFile {
  path: string;
  name: string;
  url?: string;
  contentType?: string;
  isImage: boolean;
}

export const FileUpload = ({ label, name, required, validation, config, disabled, elementDisplayId, inputDisplayId }: FileUploadFieldProps) => {
  const { control, setValue, watch } = useFormContext();
  const journeyContext = useUserJourneyContextOptional();
  const formFileUploadEnquiryId = useFormFileUploadEnquiryIdOptional();
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isLoadingExisting, setIsLoadingExisting] = useState(false);
  const validationConfig = getValidationConfig(validation);
  const description = getFieldHelperText(config.helperText, required);
  const watchedValue = watch(name) as string[] | undefined;

  const isPreviewMode = !journeyContext && !formFileUploadEnquiryId;
  const enquiryId = journeyContext?.enquiry?.id ?? formFileUploadEnquiryId ?? undefined;

  const syncFormValue = useCallback((files: UploadedFile[]) => {
    const identifiers = files.map((file) => file.path ?? file.name);
    setValue(name, identifiers, { shouldValidate: true });
  }, [name, setValue]);

  const mapApiInfo = useCallback((info: FileUploadInfo): UploadedFile => {
    const isImage = info.contentType.startsWith("image/");
    return {
      path: info.path,
      name: info.filename,
      url: info.url,
      contentType: info.contentType,
      isImage,
    };
  }, []);

  const mapPreviewValue = useCallback((value: string): UploadedFile => {
    const fileName = value.split("/").pop() ?? value;
    const image = /\.(png|jpe?g|gif|bmp|webp)$/i.test(fileName.split("?")[0] ?? "");
    return {
      path: value,
      name: fileName,
      isImage: image,
    };
  }, []);

  useEffect(() => {
    syncFormValue(uploadedFiles);
  }, [uploadedFiles, syncFormValue]);

  useEffect(() => {
    if (journeyContext || formFileUploadEnquiryId) {
      return;
    }

    const values = Array.isArray(watchedValue) ? watchedValue.filter(Boolean) : [];
    const previewFiles = values.map(mapPreviewValue);
    setUploadedFiles(previewFiles);
  }, [journeyContext, formFileUploadEnquiryId, watchedValue, mapPreviewValue]);

  useEffect(() => {
    if (!journeyContext && !formFileUploadEnquiryId) {
      return;
    }

    if (!enquiryId) {
      setUploadedFiles([]);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setIsLoadingExisting(true);
      try {
        const files = await fileUploadApi.list(enquiryId, elementDisplayId, inputDisplayId);
        if (cancelled) return;
        setUploadedFiles(files.map(mapApiInfo));
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to load uploaded files", error);
          setUploadedFiles([]);
        }
      } finally {
        if (!cancelled) {
          setIsLoadingExisting(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [journeyContext, formFileUploadEnquiryId, enquiryId, elementDisplayId, inputDisplayId, mapApiInfo]);

  const ensureEnquiryId = useCallback(async (): Promise<string | null> => {
    if (formFileUploadEnquiryId) {
      return formFileUploadEnquiryId;
    }
    if (!journeyContext) return null;
    if (journeyContext.enquiry?.id) return journeyContext.enquiry.id;
    try {
      const newEnquiry = await journeyContext.createEnquiry();
      journeyContext.updateEnquiry(newEnquiry);
      return newEnquiry.id;
    } catch (error) {
      console.error("Failed to create enquiry", error);
      return null;
    }
  }, [journeyContext, formFileUploadEnquiryId]);

  const handleUpload = useCallback(async (acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) return;

    if (!journeyContext && !formFileUploadEnquiryId) {
      console.warn("File uploads are not supported in preview mode");
      return;
    }

    const file = acceptedFiles[0];
    const enquiry = await ensureEnquiryId();
    if (!enquiry) return;

    try {
      setUploading(true);
      setUploadProgress(0);
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 100);

      const response = await fileUploadApi.upload(enquiry, file, elementDisplayId, inputDisplayId);
      clearInterval(progressInterval);
      setUploadProgress(100);

      const newFile = mapApiInfo(response);
      setUploadedFiles((prev) => {
        const existingPaths = new Set(prev.map((item) => item.path));
        if (existingPaths.has(newFile.path)) {
          return prev;
        }
        return [...prev, newFile];
      });

      setTimeout(() => {
        setUploadProgress(0);
        setUploading(false);
      }, 500);
    } catch (error) {
      console.error("File upload failed", error);
      setUploadProgress(0);
      setUploading(false);
    }
  }, [journeyContext, formFileUploadEnquiryId, elementDisplayId, inputDisplayId, ensureEnquiryId, mapApiInfo]);

  const handleRemove = useCallback(async (file: UploadedFile) => {
    if (journeyContext || formFileUploadEnquiryId) {
      try {
        const enquiry = formFileUploadEnquiryId ?? journeyContext?.enquiry?.id ?? (await ensureEnquiryId());
        if (!enquiry) return;
        await fileUploadApi.delete(enquiry, elementDisplayId, inputDisplayId, file.path);
      } catch (error) {
        console.error("Failed to delete file", error);
        return;
      }
    }

    setUploadedFiles((prev) => prev.filter((item) => item.path !== file.path));
  }, [journeyContext, formFileUploadEnquiryId, elementDisplayId, inputDisplayId, ensureEnquiryId]);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    handleUpload(acceptedFiles);
  }, [handleUpload]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    disabled: disabled || uploading || isPreviewMode,
    multiple: config.multiple || false,
    accept: config.accept ? (() => {
      const acceptMap: Record<string, string[]> = {};
      const parts = config.accept.split(",").map((p) => p.trim());
      parts.forEach((part) => {
        if (part.includes("/")) {
          acceptMap[part] = [];
        } else {
          const mimeType = part === "pdf" ? "application/pdf" :
            part === "doc" ? "application/msword" :
            part === "docx" ? "application/vnd.openxmlformats-officedocument.wordprocessingml.document" :
            part === "xls" ? "application/vnd.ms-excel" :
            part === "xlsx" ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" :
            `application/${part}`;
          acceptMap[mimeType] = [];
        }
      });
      return acceptMap;
    })() : undefined,
    maxSize: config.maxSize ? config.maxSize * 1024 * 1024 : undefined,
  });

  return (
    <FormField
      control={control}
      defaultValue={config.defaultValue || []}
      rules={{
        ...validationConfig,
        required: {
          value: required,
          message: "This is a required field",
        },
      }}
      name={name}
      render={() => (
        <FormItem className="space-y-2">
          {label && <Label htmlFor={name}>{label}</Label>}
          <FormMessage />
          <FormControl>
            <div className="space-y-4">
              <div
                {...getRootProps()}
                className={cn(
                  "border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors",
                  isDragActive && "border-primary bg-primary/5",
                  (disabled || uploading || isPreviewMode) && "opacity-50 cursor-not-allowed",
                  !isDragActive && "border-gray-300 hover:border-gray-400"
                )}
              >
                <input {...getInputProps()} id={name} aria-label={label} />
                <div className="flex flex-col items-center gap-2">
                  <Upload className="h-8 w-8 text-gray-400" />
                  {uploading ? (
                    <div className="w-full space-y-2">
                      <p className="text-sm text-gray-600">Uploading...</p>
                      <Progress value={uploadProgress} className="w-full" />
                    </div>
                  ) : (
                    <>
                      <p className="text-sm text-gray-600">
                        {isDragActive
                          ? "Drop the file here"
                          : isPreviewMode
                            ? "File uploads disabled in preview"
                            : "Drag & drop a file here, or click to select"}
                      </p>
                      {config.maxSize && (
                        <p className="text-xs text-gray-500">
                          Max size: {config.maxSize}MB
                        </p>
                      )}
                      {config.accept && (
                        <p className="text-xs text-gray-500">
                          Accepted: {config.accept}
                        </p>
                      )}
                    </>
                  )}
                </div>
              </div>

              {(isLoadingExisting || uploadedFiles.length > 0) && (
                <div className="space-y-2">
                  {isLoadingExisting && (
                    <p className="text-sm text-gray-500">Loading files...</p>
                  )}
                  {uploadedFiles.map((file) => (
                    <div
                      key={file.path}
                      className={cn(
                        "flex items-center gap-3 p-3 bg-gray-50 rounded border",
                        file.isImage && "flex-col sm:flex-row"
                      )}
                    >
                      {file.isImage ? (
                        <div className="w-full sm:w-auto flex-shrink-0">
                          {enquiryId ? (
                            <img
                              src={fileUploadApi.getProxyUrl(enquiryId, file.path)}
                              alt={file.name}
                              className="w-full h-auto sm:w-20 sm:h-20 object-cover rounded border"
                            />
                          ) : (
                            <div className="w-full aspect-square sm:w-20 sm:h-20 rounded border bg-gray-100 flex items-center justify-center">
                              <File className="h-8 w-8 text-gray-400" />
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="flex-shrink-0">
                          <File className="h-5 w-5 text-gray-500" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-700 truncate text-wrap">{file.name}</p>
                        {enquiryId ? (
                          <a
                            href={fileUploadApi.getProxyUrl(enquiryId, file.path)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-blue-600 hover:underline"
                          >
                            View file
                          </a>
                        ) : null}
                      </div>
                      {!disabled && !isPreviewMode && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => handleRemove(file)}
                          className="flex-shrink-0"
                        >
                          <X className="h-4 w-4 mr-1" />
                          Remove
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </FormControl>
          {description && <FormDescription>{description}</FormDescription>}
        </FormItem>
      )}
    />
  );
};

